// @ts-strict-ignore
import { getUserState } from '@carvana/core-auth';
import { Experiments } from '@carvana/experiment/SharedTypes';
import { datadogRum, type RumEvent } from '@datadog/browser-rum';
import Cookie from './Cookie';
import { Severity } from './datadog.types';
import type { DatadogInterface, DatadogSpan, LogMessage } from './datadog.types';
import { trackFeatureFlag } from './featureFlagTracking';

declare global {
  interface Window {
    Cypress: unknown;
  }
}

const moduleVersions: { [key: string]: string } = {};
const MODULE_NAME = 'stc-value-tracker-ui';
const ALLOWED_TRACING_URLS_BY_ENVIRONMENT: Record<string, string[]> = {
  production: ['https://apik.carvana.io'],
  test: ['https://apik.carvanatech.com']
};
const GRAPHQL_URLS_BY_ENVIRONMENT: Record<string, RegExp[]> = {
  production: [
    /^https:\/\/apik\.carvana\.io\/stc\/bff\/graphql\/?(?:\?.*)?$/,
    /^https:\/\/apik\.carvana\.io\/stc\/offer\/graphql\/?(?:\?.*)?$/
  ],
  test: [
    /^https:\/\/apik\.carvanatech\.com\/stc\/bff\/graphql\/?(?:\?.*)?$/,
    /^https:\/\/apik\.carvanatech\.com\/stc\/offer\/graphql\/?(?:\?.*)?$/
  ]
};
const IMAGE_FOR_YMMT_PATH_PATTERN =
  /^(\/stc\/trades\/api\/v5\/vehicleconfiguration\/imageforYMMT)\/[^/]+\/[^/]+\/[^/]+\/[^/]+\/?$/i;
const NORMALIZED_IMAGE_FOR_YMMT_PATH =
  '/stc/trades/api/v5/vehicleconfiguration/imageforYMMT/:year/:make/:model/:trim';

type GraphQLConfiguration = {
  allowedGraphQlUrls?: {
    match: RegExp;
    trackPayload: boolean;
    trackResponseErrors: boolean;
  }[];
};
const REDACTED_GRAPHQL_VARIABLES = '[REDACTED]';

const getAllowedTracingUrls = () =>
  ALLOWED_TRACING_URLS_BY_ENVIRONMENT[process.env.CARVANA_APP_ENVIRONMENT ?? ''];

const getTracingConfiguration = () => {
  const allowedTracingUrls = getAllowedTracingUrls();

  if (!allowedTracingUrls) {
    return {};
  }

  return {
    allowedTracingUrls,
    propagateTraceBaggage: true,
    traceContextInjection: 'all' as const,
    traceSampleRate: 100
  };
};

const getGraphQLConfiguration = (): GraphQLConfiguration => {
  const matches = GRAPHQL_URLS_BY_ENVIRONMENT[process.env.CARVANA_APP_ENVIRONMENT ?? ''];

  if (!matches) {
    return {};
  }

  return {
    allowedGraphQlUrls: matches.map(match => ({
      match,
      trackPayload: false,
      trackResponseErrors: true
    }))
  };
};

const normalizeRumResourceUrl = (resourceUrl: string) => {
  if (!resourceUrl.toLowerCase().includes('/imageforymmt/')) {
    return resourceUrl;
  }

  try {
    const url = new URL(resourceUrl);

    if (IMAGE_FOR_YMMT_PATH_PATTERN.test(url.pathname)) {
      url.pathname = NORMALIZED_IMAGE_FOR_YMMT_PATH;
      return url.toString();
    }
  } catch {
    return resourceUrl;
  }

  return resourceUrl;
};

const prepareRumResourceEvent = (event: RumEvent) => {
  if (event.type !== 'resource') {
    return;
  }

  event.resource.url = normalizeRumResourceUrl(event.resource.url);

  if (typeof event.resource.graphql?.variables === 'string') {
    event.resource.graphql.variables = REDACTED_GRAPHQL_VARIABLES;
  }
};

const normalizeRumViewName = (viewUrl: string, currentViewName: string) => {
  try {
    const { pathname } = new URL(viewUrl, 'https://www.carvana.com');

    if (/^\/(?:value-tracker\/)?track(?:\/|$)/.test(pathname)) {
      return '/value-tracker/track';
    }

    if (/^\/(?:value-tracker\/)?edit\/forecast(?:\/|$)/.test(pathname)) {
      return '/value-tracker/edit/forecast';
    }
  } catch {
    // Preserve the SDK-computed name when a non-standard URL cannot be parsed.
  }

  return currentViewName;
};

const syncDatadogUser = (userId?: string) => {
  if (typeof document === 'undefined') {
    return;
  }

  const bcid = Cookie.getBcId();

  if (userId || bcid) {
    datadogRum.setUser({
      ...(userId ? { id: userId } : {}),
      ...(bcid ? { bcid } : {})
    });
    return;
  }

  datadogRum.clearUser();
};

const initializeDataDog = (experiments: Experiments) => {
  const isClient = typeof window === 'object';
  const { user } = getUserState();
  let existingCypress;
  if (isClient) {
    existingCypress = window.Cypress;
    window.Cypress = undefined;
  }

  datadogRum.init({
    ...getTracingConfiguration(),
    ...getGraphQLConfiguration(),
    applicationId: 'c0e19456-086b-48eb-b954-ea6abf661dcf',
    clientToken: 'pub1d8c8908f469710592832a49e98cf269',
    site: 'datadoghq.com',
    service: MODULE_NAME,
    env: `${process.env.CARVANA_APP_ENVIRONMENT}`,
    version: `${process.env.APP_VERSION}`,
    sessionSampleRate: 100,
    sessionReplaySampleRate: 100,
    trackUserInteractions: true,
    defaultPrivacyLevel: 'mask-user-input',
    trackLongTasks: true,
    trackResources: true,
    beforeSend: event => {
      prepareRumResourceEvent(event);
      event.view.name = normalizeRumViewName(event.view.url, event.view.name);
      return true;
    }
  });

  syncDatadogUser(user.user_id);
  const experimentWithAssignedBucket = Object.entries(experiments).reduce(
    (acc, [key, value]) => ({
      ...acc,
      [key]: value.assignedBucket
    }),
    {}
  );
  datadogRum.setGlobalContextProperty('vtExperiments', experimentWithAssignedBucket);

  Object.entries(experiments).forEach(([experimentName, experimentData]) => {
    trackFeatureFlag(experimentName, experimentData.assignedBucket);
  });

  datadogRum.addAction('Rum Initialized');

  if (isClient) {
    window.Cypress = existingCypress;
  }
};

const userAuthenticated = (userId: string) => {
  syncDatadogUser(userId);
};
const clearAuthenticatedUser = () => {
  syncDatadogUser();
};
const startTrackingEvent = (component: string, name: string) => {
  const start = performance.now();
  return () => {
    const taken = performance.now() - start;
    datadogRum.addAction(`${component}:${name}`, {
      component,
      name,
      taken
    });
  };
};

const log = (message: LogMessage) => {
  if (message.severity >= Severity.Error) {
    datadogRum.addError(message.message, message);
  }
  datadogRum.addAction('LogMessage', message);
};

const logError = (err: unknown, message?: string) =>
  datadogRum.addError(err, {
    message
  });

const recordException = (exception: unknown, properties: object) =>
  datadogRum.addError(exception, properties);

const addAction = (eventName: string, context?: object) => {
  datadogRum.addAction(eventName, context);
};

export class DataDogSpanImpl implements DatadogSpan {
  private readonly measureStart: DOMHighResTimeStamp;

  private readonly start: Date;

  private readonly spanName: string;

  private readonly component: string;

  private measureEnd?: DOMHighResTimeStamp;

  private ended?: Date;

  constructor(component: string, name: string) {
    this.spanName = name;
    this.component = component;
    this.measureStart = performance.now();
    this.start = new Date();
  }

  private defaultParameters() {
    return {
      component: this.component,
      spanName: this.spanName,
      start: this.start
    };
  }

  addEvent(name: string, attributes?: unknown): this {
    const elapsedMs = performance.now() - this.measureStart;
    datadogRum.addAction(`${this.component}:${this.spanName}:${name}`, {
      ...this.defaultParameters(),
      name,
      elapsedMs,
      attributes: attributes ?? {}
    });
    return this;
  }

  addErrorEvent(name: string, attributes?: unknown, severity?: Severity): this {
    const elapsed = performance.now() - this.measureStart;
    datadogRum.addError(`${this.component}:${this.spanName}:${name}`, {
      severity: severity ?? Severity.Error,
      elapsed,
      attributes: attributes ?? {}
    });

    return this;
  }

  end(attributes?: object): void {
    if (this.measureEnd) {
      return;
    }
    this.measureEnd = performance.now();
    this.ended = new Date();
    const elapsedMs = performance.now() - this.measureStart;

    datadogRum.addAction(`${this.component}:${this.spanName}`, {
      ...this.defaultParameters(),
      ended: this.ended,
      elapsedMs,
      ...(attributes || {})
    });
  }
}

const startSpan = (component: string, name: string): DatadogSpan =>
  new DataDogSpanImpl(component, name);

const addVersion = (moduleName: string, version: string) => {
  moduleVersions[moduleName] = version;
};

const addToRumGlobalContext = (key: string, value: object | string) => {
  datadogRum.setGlobalContextProperty(key, value);
};

const getRumGlobalContext = (key: string) => datadogRum.getGlobalContext()[key];

const datadogInterface: DatadogInterface = {
  setAuthenticatedUser: userAuthenticated,
  clearAuthenticatedUser,
  startSpan,
  log,
  logError,
  recordException,
  addAction,
  addVersion,
  addToRumGlobalContext,
  getRumGlobalContext
};

export {
  clearAuthenticatedUser,
  DatadogSpan,
  DatadogInterface,
  datadogInterface,
  getAllowedTracingUrls,
  getGraphQLConfiguration,
  getTracingConfiguration,
  initializeDataDog,
  log,
  logError,
  recordException,
  normalizeRumViewName,
  normalizeRumResourceUrl,
  prepareRumResourceEvent,
  startSpan,
  startTrackingEvent,
  syncDatadogUser,
  userAuthenticated
};
